import {
  interestRateModelV2Abi,
  siloRepositoryAbi,
} from "@silo/contract-abi-types";
import { siloRepositoryAddress } from "@silo/contract-addresses";
import {
  Address,
  ChainPublicClientService,
  IrmConfig,
  PercentFromBigInt,
  ProtocolKeyService,
} from "@silo/types";
import { Effect, pipe } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "./types";
import { Schema } from "@effect/schema";

export type GetIrmMeta = ReturnType<typeof getIrmMeta>;
export type GetIrmMetaSuccess = Effect.Effect.Success<GetIrmMeta>;
export type GetIrmMetaError = Effect.Effect.Error<GetIrmMeta>;
export type GetIrmMetaContext = Effect.Effect.Context<GetIrmMeta>;

interface GetIrmMetaParams {
  marketAddress: Address;
  tokenAddress: Address;
}

export interface IrmMeta {
  irmAddress: Address;
  irmConfig: IrmConfig;
}

export function getIrmMeta({ marketAddress, tokenAddress }: GetIrmMetaParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;

    const siloRepositoryContract = getContract({
      address: siloRepositoryAddress[protocolKey],
      abi: siloRepositoryAbi,
      client: publicClient,
    });

    const viemIrmMetaEncoded = yield* Effect.tryPromise({
      try: async () => {
        const irmAddress =
          await siloRepositoryContract.read.getInterestRateModel([
            marketAddress,
            tokenAddress,
          ]);

        const irmContract = getContract({
          address: irmAddress,
          abi: interestRateModelV2Abi,
          client: publicClient,
        });

        const irmConfig = await irmContract.read.getConfig([
          marketAddress,
          tokenAddress,
        ]);

        const encoded: ViemIrmMetaEncoded = {
          irmAddress,
          irmConfig,
        };

        return encoded;
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const irmMeta: IrmMeta = yield* pipe(
      viemIrmMetaEncoded,
      Schema.decode(ViemIrmMeta)
    );

    return irmMeta;
  });
}

export type ViemIrmMeta = typeof ViemIrmMeta.Type;
export type ViemIrmMetaEncoded = typeof ViemIrmMeta.Encoded;

export const ViemIrmMeta = Schema.Struct({
  irmAddress: Address,
  irmConfig: Schema.Struct({
    uopt: PercentFromBigInt,
    ucrit: PercentFromBigInt,
    ulow: PercentFromBigInt,
    ki: Schema.BigIntFromSelf,
    kcrit: Schema.BigIntFromSelf,
    klow: Schema.BigIntFromSelf,
    klin: Schema.BigIntFromSelf,
    beta: Schema.BigIntFromSelf,
    ri: Schema.BigIntFromSelf,
    Tcrit: Schema.BigIntFromSelf,
  }),
}).annotations({ identifier: "ViemIrmMeta" });
